/**
 * ProvisionTenantModal (OCP-663) - the operator provisions a managed tenant
 * for an existing portal account without a portal redeem. Same body as the
 * `ocp_provision_tenant` MCP tool; `POST /v1/admin/tenants`.
 */
import { useState } from 'react';

import { api, ApiError } from '../lib/api';

export interface ProvisionTenantResult {
  tenant_id: string;
  subdomain: string;
  url: string;
  magic_login_url: string;
  purpose: 'scratch' | null;
  scratch_expires_at: string | null;
  account: { id: string; email: string };
  contract_gates_skipped: boolean;
}

const GATE_ERRORS: Record<string, string> = {
  responsible_contact_missing: 'The account has no responsible contact named. Tick "Skip contract gates" to provision anyway (audited).',
  terms_acceptance_required: 'The account has not accepted the current Terms. Tick "Skip contract gates" to provision anyway (audited).',
  terms_not_published: 'No Terms version is published. Tick "Skip contract gates" to provision anyway (audited).',
  avv_not_published: 'No AVV (data processing agreement) version is published; the AVV is mandatory. Tick "Skip contract gates" to provision anyway (audited).',
  business_capacity_missing: 'The account has not declared its business capacity. Tick "Skip contract gates" to provision anyway (audited).',
  company_name_missing: 'The account has no company name. Tick "Skip contract gates" to provision anyway (audited).',
  consumer_suspected: 'The account is flagged consumer-suspected; clear the flag first.',
  account_not_found: 'No portal account with that id / email.',
  code_unknown: 'Unknown code.',
  code_expired: 'The code has expired.',
  code_exhausted: 'The code has no uses left.',
  code_already_redeemed_by_customer: 'This account already redeemed that code.',
  subdomain_taken: 'That subdomain is taken.',
  reserved_subdomain: 'That subdomain is reserved.',
  subdomain_required: 'Subdomain: 3-40 chars, a-z 0-9 and hyphens.',
  invalid_team_size: 'Unknown team-size bucket.',
};

export function ProvisionTenantModal({
  onClose,
  onProvisioned,
}: {
  onClose: () => void;
  onProvisioned: (result: ProvisionTenantResult) => void;
}) {
  const [accountEmail, setAccountEmail] = useState('');
  const [code, setCode] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [locale, setLocale] = useState<'en' | 'de'>('en');
  const [company, setCompany] = useState('');
  const [scratch, setScratch] = useState(false);
  const [scratchDays, setScratchDays] = useState('7');
  const [skipGates, setSkipGates] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = accountEmail.trim() && code.trim() && subdomain.trim() && reason.trim().length >= 5;

  const submit = async () => {
    if (!ready) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        account_email: accountEmail.trim(),
        code: code.trim(),
        subdomain: subdomain.trim().toLowerCase(),
        locale,
        reason: reason.trim(),
      };
      if (teamSize.trim()) body.team_size = teamSize.trim();
      if (company.trim()) body.company = company.trim();
      if (scratch) {
        body.purpose = 'scratch';
        const d = Number.parseInt(scratchDays, 10);
        if (Number.isFinite(d) && d > 0) body.scratch_days = d;
      }
      if (skipGates) body.skip_contract_gates = true;
      const r = await api<ProvisionTenantResult>('/v1/admin/tenants', { method: 'POST', body });
      onProvisioned(r);
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.body as { error?: string } | null)?.error;
        setError((code && GATE_ERRORS[code]) ?? `Could not provision (HTTP ${err.status}${code ? `, ${code}` : ''}).`);
      } else {
        setError('Could not provision the tenant.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const input =
    'w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">New tenant</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Provisions a managed tenant for an existing portal account through the same pipeline as a
          customer redeem: the code is consumed, the instance is built, the setup link is returned once.
          Every field here is also on the <code>ocp_provision_tenant</code> tool.
        </p>

        <div className="mt-4 space-y-3">
          <Labelled label="Account email" required>
            <input type="email" value={accountEmail} onChange={(e) => setAccountEmail(e.target.value)} placeholder="owner@example.com" autoComplete="off" className={input} data-testid="provision-account-email" />
          </Labelled>
          <div className="grid grid-cols-2 gap-3">
            <Labelled label="Code" required hint="prepaid / invite, as issued">
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="ORBOTO-XXXX-XXXX-XXXX" autoComplete="off" className={input} data-testid="provision-code" />
            </Labelled>
            <Labelled label="Subdomain" required>
              <input type="text" value={subdomain} onChange={(e) => setSubdomain(e.target.value)} placeholder="studio" autoComplete="off" className={input} data-testid="provision-subdomain" />
            </Labelled>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Labelled label="Team size" hint="bucket">
              <input type="text" value={teamSize} onChange={(e) => setTeamSize(e.target.value)} placeholder="1-9" autoComplete="off" className={input} />
            </Labelled>
            <Labelled label="Locale">
              <select value={locale} onChange={(e) => setLocale(e.target.value === 'de' ? 'de' : 'en')} className={input}>
                <option value="en">en</option>
                <option value="de">de</option>
              </select>
            </Labelled>
            <Labelled label="Company" hint="optional">
              <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="off" className={input} />
            </Labelled>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={scratch} onChange={(e) => setScratch(e.target.checked)} data-testid="provision-scratch" />
            Scratch tenant (throwaway, listed for hard-delete after
            <input type="number" min={1} max={90} value={scratchDays} onChange={(e) => setScratchDays(e.target.value)} disabled={!scratch} className="w-16 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-1 py-0.5 text-sm" />
            days)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={skipGates} onChange={(e) => setSkipGates(e.target.checked)} data-testid="provision-skip-gates" />
            Skip contract gates (responsible contact, Terms, business capacity) - audited
          </label>
          <Labelled label="Reason" required hint="audited">
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Scratch tenant for the showcase recording (ORB-2084)" autoComplete="off" className={input} data-testid="provision-reason" />
          </Labelled>
        </div>

        {error && (
          <div role="alert" className="mt-3 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60">
            Cancel
          </button>
          <button type="button" onClick={() => void submit()} disabled={submitting || !ready} data-testid="provision-submit" className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-brand-300">
            {submitting ? 'Provisioning…' : 'Provision tenant'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The one-time result view: the setup link is shown here and never again. */
export function ProvisionTenantResultModal({ result, onClose }: { result: ProvisionTenantResult; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tenant provisioning started</h2>
        <dl className="mt-3 space-y-2 text-sm">
          <div><dt className="text-gray-500">Tenant</dt><dd data-testid="provision-result-url">{result.url}</dd></div>
          <div><dt className="text-gray-500">Account</dt><dd>{result.account.email}</dd></div>
          {result.purpose === 'scratch' && (
            <div><dt className="text-gray-500">Scratch, due for hard-delete</dt><dd>{result.scratch_expires_at?.slice(0, 10)}</dd></div>
          )}
          <div>
            <dt className="text-gray-500">First-boot setup link (shown once, hand it to the account holder)</dt>
            <dd className="break-all font-mono text-xs" data-testid="provision-result-magic">{result.magic_login_url}</dd>
          </div>
        </dl>
        <p className="mt-3 text-xs text-gray-500">
          The instance builds in the background; it shows as active on the tenant page once ready. The link can be re-sent from the tenant detail page.
        </p>
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={onClose} className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600">Done</button>
        </div>
      </div>
    </div>
  );
}

function Labelled({ label, hint, required, children }: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {hint && <span className="ml-1 text-xs text-gray-500">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
